import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServiceStore } from '../../store/useServiceStore';
import { usePriceIndexStore } from '../../store/usePriceIndexStore';
import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Badge } from '../../components/Shared/Badge';
import { Button } from '../../components/Shared/Button';
import { SlideOver } from '../../components/Shared/SlideOver';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Pagination } from '../../components/Shared/Pagination';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import PriceIndexDialog from '../../components/Shared/PriceIndexDialog';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table';
import { Plus, Search, Edit2, Trash2, TrendingUp, Package, History } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { ServiceForm } from './components/ServiceForm';
import { useServiceList } from './hooks/useServiceList';
import { formatMoney } from '../../utils/money';
import type { FacilityFlowCoreModulesServicesDtosServiceDto } from '../../api/generated/models';

export default function ServicesPage() {
  const { t } = useTranslation(['services', 'common']);
  const navigate = useNavigate();
  const { deleteService } = useServiceStore();
  const { activePriceIndex, fetchActivePriceIndex, createPriceIndex } = usePriceIndexStore();
  const {
    loading, error, initialLoadComplete, reloadServices, searchTerm, setSearchTerm, handleSearch,
    filterStatus, setFilterStatus, filteredServices, page, pageCount, pageItems, goToPage,
  } = useServiceList();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPriceIndexDialogOpen, setIsPriceIndexDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<FacilityFlowCoreModulesServicesDtosServiceDto | null>(null);

  useEffect(() => {
    fetchActivePriceIndex();
  }, [fetchActivePriceIndex]);

  const openDeleteDialog = (service: FacilityFlowCoreModulesServicesDtosServiceDto) => {
    setSelectedService(service);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedService?.id) return;
    await deleteService(selectedService.id);
    setIsDeleteDialogOpen(false);
    setSelectedService(null);
  };

  const handleEdit = (service: FacilityFlowCoreModulesServicesDtosServiceDto) => {
    setSelectedService(service);
    setIsEditModalOpen(true);
  };

  const getUnitLabel = (unit: string) => {
    const labels: Record<string, string> = {
      sqm: 'm²',
      hour: 'Stunde',
      piece: 'Stück',
    };
    return labels[unit] || unit;
  };

  const handleCreatePriceIndex = async (data: {
    designation: string;
    factor: number;
    effectiveFrom: string;
    description?: string;
  }) => {
    const createdIndex = await createPriceIndex(data);
    await fetchActivePriceIndex();
    
    // Redirect zur Vorschau wenn erfolgreich erstellt
    if (createdIndex?.id) {
      navigate(`/price-indexes/${createdIndex.id}/preview`);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (loading && !initialLoadComplete) return <Loading />;

  if (error && !initialLoadComplete) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
            {t('services:title')}
          </h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {t('services:subtitle')}
          </p>
        </div>
        <Card>
          <EmptyState
            variant="error"
            title={t('services:errors.loadError')}
            description={error || t('services:errors.loadErrorDescription')}
            onRetry={() => reloadServices()}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('services:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('services:subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              variant="secondary"
              leftIcon={<History className="w-4 h-4" strokeWidth={2} />}
              onClick={() => navigate('/price-indexes/history')}
              className="w-full sm:w-auto justify-center"
            >
              <span className="hidden md:inline">{t('services:buttons.priceIndexHistory')}</span>
              <span className="md:hidden">{t('services:buttons.historyShort')}</span>
            </Button>
            <Button
              variant="secondary"
              leftIcon={<TrendingUp className="w-4 h-4" strokeWidth={2} />}
              onClick={() => setIsPriceIndexDialogOpen(true)}
              className="w-full sm:w-auto justify-center"
            >
              <span className="hidden md:inline">{t('services:buttons.newPriceIndex')}</span>
              <span className="md:hidden">{t('services:buttons.priceIndexShort')}</span>
            </Button>
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              onClick={() => setIsCreateModalOpen(true)}
              className="w-full sm:w-auto justify-center"
            >
              <span className="hidden sm:inline">{t('services:newService')}</span>
              <span className="sm:hidden">{t('common:actions.create')}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Active Price Index Info */}
      {activePriceIndex && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-emerald-100 rounded-md flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-emerald-700" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h3 className="text-[13px] font-medium text-slate-900 truncate">
                  {activePriceIndex.designation}
                </h3>
                <p className="text-[12px] text-slate-600 tabular-nums">
                  {t('services:priceIndex.factor')}: {activePriceIndex.factor?.toFixed(2)} · {t('services:priceIndex.validFrom')}: {formatDate(activePriceIndex.effectiveFrom)}
                </p>
              </div>
            </div>
            <Badge variant="success">{t('common:status.active')}</Badge>
          </div>
        </div>
      )}

      {/* Filters & Search — single card matching the rest of the app */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
              <input
                type="text"
                placeholder={t('services:placeholders.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-9 pr-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('services:filters.statusLabel')}
            className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          >
            <option value="all">{t('common:filters.allStatus')}</option>
            <option value="active">{t('common:status.active')}</option>
            <option value="inactive">{t('common:status.inactive')}</option>
          </select>
        </div>
      </div>

      {/* Services Table */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('services:messages.loadError')}
            description={error || t('common:errors.tryAgain')}
            onRetry={() => reloadServices()}
          />
        </Card>
      ) : filteredServices.length === 0 ? (
        <EmptyState
          icon={<Package className="w-12 h-12" strokeWidth={1.75} />}
          title={t('services:empty.title')}
          description={t('services:empty.description')}
          action={
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t('services:createService')}
            </Button>
          }
        />
      ) : (
        <>
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader className="hidden sm:table-cell">{t('services:table.number')}</TableHeader>
                <TableHeader>{t('services:table.name')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('services:table.unit')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('services:table.basePrice')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('services:table.duration')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('common:status.label')}</TableHeader>
                <TableHeader>{t('common:actions.title')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((service) => (
                <TableRow key={service.id}>
                  <TableCell className="hidden sm:table-cell">
                    <span className="font-mono-num text-[12px] text-slate-500">
                      {service.serviceNumber || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-[13px] text-slate-900">
                      {service.name}
                    </div>
                    <div className="text-[11px] text-slate-500 sm:hidden mt-0.5 tabular-nums">
                      {service.serviceNumber}
                      {service.basePrice ? ` · ${formatMoney(service.basePrice)}` : ''}
                    </div>
                    {service.description && (
                      <div className="hidden sm:block text-[12px] text-slate-500 line-clamp-1 mt-0.5">
                        {service.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-[13px] text-slate-600">
                      {service.unit ? getUnitLabel(service.unit) : '—'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-[13px] font-medium text-slate-900 tabular-nums">
                      {formatMoney(service.basePrice)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-[13px] text-slate-600 tabular-nums">
                      {service.estimatedDurationMinutes ? `${service.estimatedDurationMinutes} min` : '—'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={service.status === 'Active' ? 'success' : 'default'}>
                      {service.status === 'Active' ? t('common:status.active') : t('common:status.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(service)}
                        title={t('common:actions.edit')}
                        aria-label={t('common:actions.edit')}
                        className="p-1.5"
                      >
                        <Edit2 className="w-4 h-4" strokeWidth={2} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(service)}
                        title={t('common:actions.delete')}
                        aria-label={t('common:actions.delete')}
                        className="p-1.5"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <Pagination
          page={page}
          pageCount={pageCount}
          totalCount={filteredServices.length}
          onPageChange={goToPage}
          disabled={loading}
        />
        </>
      )}

      {/* Create SlideOver */}
      <SlideOver
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={t('services:createService')}
      >
        <ServiceForm
          onSuccess={() => {
            setIsCreateModalOpen(false);
            reloadServices();
          }}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </SlideOver>

      {/* Edit SlideOver */}
      <SlideOver
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedService(null);
        }}
        title={t('services:editService')}
      >
        {selectedService && (
          <ServiceForm
            service={selectedService}
            onSuccess={() => {
              setIsEditModalOpen(false);
              setSelectedService(null);
              reloadServices();
            }}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedService(null);
            }}
          />
        )}
      </SlideOver>

      {/* Price Index Dialog */}
      <PriceIndexDialog
        isOpen={isPriceIndexDialogOpen}
        onClose={() => setIsPriceIndexDialogOpen(false)}
        onSubmit={handleCreatePriceIndex}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedService(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('services:deleteService')}
        message={t('services:confirmDelete', { name: selectedService?.name || '' })}
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
      />
    </div>
  );
}
